import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import toast from "react-hot-toast";
import { useRequestPasswordReset } from "utils/api/auth.api";

interface ForgotPasswordProps {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
}

export default function ForgotPassword({ isOpen, setIsOpen }: ForgotPasswordProps) {
    const { mutate: requestPasswordReset, isLoading, isSuccess, isError, error } =
        useRequestPasswordReset();

    const [email, setEmail] = useState("");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
    };

    const handleSubmit = () => {
        if (!email) {
            toast.error("Email is required");
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            toast.error("Please enter a valid email address");
            return;
        }

        requestPasswordReset(email);
    };

    useEffect(() => {
        if (isSuccess) {
            toast.success("Password reset request has been sent to Super Admin. You will be notified once your password is reset.");
            setEmail("");
            setIsOpen(false);
        }
    }, [isSuccess, setIsOpen]);

    useEffect(() => {
        if (isError && error) {
            toast.error(
                error?.message || "Failed to send password reset request. Please try again."
            );
        }
    }, [isError, error]);

    const handleClose = () => {
        setEmail("");
        setIsOpen(false);
    };

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[9999]" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-[9999]" />
                </Transition.Child>

                <div className="fixed inset-0 z-[9999] w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative z-[10000] transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md">
                                <div className="bg-white px-4 pb-4 pt-5 sm:p-4 sm:pb-4">
                                    <div className="">
                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                            <Dialog.Title
                                                as="h3"
                                                className="text-base font-semibold leading-6 text-gray-900"
                                            >
                                                Forgot Password?
                                            </Dialog.Title>
                                            <div className="mt-4">
                                                <div className="mt-2">
                                                    <p className="text-sm text-gray-600 mb-4">
                                                        Enter your email address and we will send a password reset request to the Super Admin. You will be notified once your password is reset.
                                                    </p>
                                                    <label
                                                        htmlFor="email"
                                                        className="block text-sm font-medium leading-6 text-gray-900"
                                                    >
                                                        Email <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="mt-2">
                                                        <input
                                                            id="email"
                                                            name="email"
                                                            type="email"
                                                            value={email}
                                                            onChange={handleChange}
                                                            required
                                                            placeholder="Enter your email address"
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-0 ps-2 placeholder:text-gray-400 sm:text-sm sm:leading-6"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <button
                                        type="button"
                                        disabled={isLoading}
                                        className="inline-flex w-full justify-center rounded-md bg-cyan-300 px-3 py-2 text-sm font-semibold text-black shadow-sm hover:bg-cyan-800 sm:ml-3 sm:w-auto ring-1 ring-inset ring-cyan-800 disabled:opacity-50"
                                        onClick={handleSubmit}
                                    >
                                        {isLoading ? (
                                            <div className="w-5 h-5 rounded-full animate-spin mx-auto border-2 border-solid border-black-800 border-t-transparent"></div>
                                        ) : (
                                            "Send Request"
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                        onClick={handleClose}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

